import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, Shield, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

export const LoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show2FA, setShow2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setShow2FA(true);
      toast.success('Please enter your 2FA code');
    }, 600);
  };

  const handle2FAInput = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newCode = [...twoFACode];
    newCode[index] = value;
    setTwoFACode(newCode);
    
    if (value && index < 5) {
      document.getElementById(`code-${index + 1}`)?.focus();
    }
    
    if (newCode.every(digit => digit !== '') && index === 5) {
      setTimeout(() => {
        setIsLoading(true);
        setTimeout(() => {
          toast.success('Login successful!');
          navigate('/dashboard');
        }, 400);
      }, 150);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !twoFACode[index] && index > 0) {
      document.getElementById(`code-${index - 1}`)?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;
    
    const newCode = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
    setTwoFACode(newCode);
    
    if (newCode.every(digit => digit !== '')) {
      setTimeout(() => {
        setIsLoading(true);
        setTimeout(() => {
          toast.success('Login successful!');
          navigate('/dashboard');
        }, 400);
      }, 150);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {!show2FA ? (
          <Card className="border-slate-200 shadow-xl">
            <CardHeader className="space-y-1 text-center pb-8">
              <div className="mx-auto mb-4 h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="text-2xl font-bold">PropFirmHouse Admin</CardTitle>
              <CardDescription>Sign in to access the admin panel</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="admin@propfirmhouse.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-9"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-9"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign in
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-slate-200 shadow-xl">
            <CardHeader className="space-y-1 text-center pb-8">
              <div className="mx-auto mb-4 h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="text-2xl font-bold">Two-Factor Authentication</CardTitle>
              <CardDescription>
                Enter the 6-digit code from your authenticator app
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <Label className="text-center block">Verification Code</Label>
                <div className="flex gap-2 justify-center" onPaste={handlePaste}>
                  {twoFACode.map((digit, index) => (
                    <Input
                      key={`2fa-input-${index}`}
                      id={`code-${index}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handle2FAInput(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className="w-11 h-12 text-center text-lg font-semibold"
                      autoFocus={index === 0}
                      disabled={isLoading}
                    />
                  ))}
                </div>
                <p className="text-xs text-center text-muted-foreground">
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="h-3 w-3 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                      Verifying code...
                    </span>
                  ) : (
                    'Code will be verified automatically'
                  )}
                </p>
              </div>

              <Button
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setShow2FA(false);
                  setTwoFACode(['', '', '', '', '', '']);
                  setIsLoading(false);
                }}
                disabled={isLoading}
              >
                Back to Sign In
              </Button>
            </CardContent>
          </Card>
        )}
        
        <p className="mt-4 text-center text-sm text-muted-foreground">
          PropFirmHouse © 2026. All rights reserved.
        </p>
      </div>
    </div>
  );
};
